import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Content,
  fetchEntries,
  fetchOneEntry,
  getBuilderSearchParams,
} from "@builder.io/sdk-react/edge";
import { clientEnv } from "~/lib/utils";
import { CUSTOM_COMPONENTS } from "~/components/builder-registry";
import { createBrandMeta } from "~/lib/shared/meta";

export const BUILDER_MODEL = "page";

const BuilderCatchAllPage = () => {
  const { name } = Route.useParams();
  const search = Route.useSearch();
  const urlPath = name ? `/page/${name}` : "/";

  const { data: content } = useQuery({
    queryKey: ["builder-content", urlPath],
    queryFn: async () => {
      const builderContent = await fetchOneEntry({
        model: BUILDER_MODEL,
        apiKey: clientEnv.VITE_BUILDER_API_KEY,
        userAttributes: { urlPath },
        options: getBuilderSearchParams(search),
      });

      if (!builderContent) {
        // Try to find redirect
        const redirectEntry = await fetchOneEntry({
          model: "url-redirects",
          apiKey: clientEnv.VITE_BUILDER_API_KEY,
          query: {
            "data.sourceUrl": urlPath,
          },
          options: getBuilderSearchParams(search),
        });

        if (redirectEntry?.data) {
          throw redirect({
            to: redirectEntry.data.destinationUrl,
            statusCode: redirectEntry.data.redirectToPermanent ? 301 : 302,
          });
        }

        throw notFound();
      }

      return builderContent;
    },
    staleTime: 1000 * 60 * 3, // 3 minutes
  });

  if (!content) {
    return <div>Loading...</div>;
  }

  return (
    <Content
      model={BUILDER_MODEL}
      content={content}
      apiKey={clientEnv.VITE_BUILDER_API_KEY}
      customComponents={CUSTOM_COMPONENTS}
    />
  );
};

export const Route = createFileRoute("/_public/page/$name/")({
  loaderDeps: ({ search }) => {
    return {
      search,
    };
  },
  loader: async ({ context, params, deps }) => {
    const urlPath = params.name ? `/page/${params.name}` : "/";

    const builderContent = await fetchOneEntry({
      model: BUILDER_MODEL,
      apiKey: clientEnv.VITE_BUILDER_API_KEY,
      userAttributes: { urlPath },
      includeUnpublished: false,
      options: getBuilderSearchParams(deps.search),
    });

    if (!builderContent) {
      // Try to find redirect
      const redirectEntry = await fetchOneEntry({
        model: "url-redirects",
        apiKey: clientEnv.VITE_BUILDER_API_KEY,
        query: {
          "data.sourceUrl": urlPath,
        },
        options: getBuilderSearchParams(deps.search),
      });

      if (redirectEntry?.data) {
        throw redirect({
          to: redirectEntry.data.destinationUrl,
          statusCode: redirectEntry.data.redirectToPermanent ? 301 : 302,
        });
      }

      throw notFound();
    }

    // Pre-load data into React Query
    await context.queryClient.ensureQueryData({
      queryKey: ["builder-content", urlPath],
      queryFn: () => builderContent,
    });

    return {
      content: builderContent,
    };
  },
  head: ({ loaderData }) => {
    const { content } = loaderData ?? {};
    const pageData = content?.data;

    // Fallback meta if no content
    if (!pageData) {
      return createBrandMeta({
        title: "Dream Coach Match - Life & Business Coaching",
        description:
          "Find your perfect life or business coach. Connect with certified professionals who can help you achieve your goals.",
        ogType: "website",
      });
    }

    // Extract meta information from Builder.io content
    const title = pageData.title || pageData.name || "Dream Coach Match";
    const description =
      pageData.description ||
      pageData.excerpt ||
      "Find your perfect life or business coach. Connect with certified professionals who can help you achieve your goals.";
    const image = pageData.image || pageData.heroImage;
    const url = `https://dreamcoachmatch.com/page/${content.data?.url?.replace(/^\/?page\/?/, "") || ""}`;

    return createBrandMeta({
      title,
      description,
      ogImage: image,
      ogUrl: url,
      canonical: url,
      keywords: pageData.keywords || pageData.tags,
      ogType: "website",
    });
  },
  component: BuilderCatchAllPage,
});

// Static generation for TanStack Start
export const generateStaticParams = async () => {
  const pages = await fetchEntries({
    model: "page",
    apiKey: clientEnv.VITE_BUILDER_API_KEY,
    omit: "data.blocks",
  });

  return pages
    .filter((a) => !!a.data)
    .map((page) => ({
      path: page
        .data!.url.replace(/^\/?page\/?/, "")
        .split("/")
        .filter(Boolean),
    }));
};
